import React, { useMemo } from "react";
import { Printer } from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import useQuickLoan from "../hooks/useQuickLoan";
import useLoanRepaymentSchedule from "../hooks/useLoanRepaymentSchedule";
import { useAuth } from "../context/AuthContext";
import { HashLoader } from "react-spinners";
import { formatAsMoney } from "../helpers/numbers";
import { formatPaymentDue } from "../helpers/dates";
import type { QuickLoanApi, QuickLoanRepaymentScheduleItem } from "../@types";
import { normalizeRepaymentSchedule, parseScheduleAmount } from "../helpers/loanSchedule";
import moment from "moment";

interface OfferLetterProps {
  loanProp?: QuickLoanApi;
  scheduleProp?: QuickLoanRepaymentScheduleItem[];
  isEmbedded?: boolean;
}

const OfferLetter: React.FC<OfferLetterProps> = ({ loanProp, scheduleProp, isEmbedded }) => {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get("id");

  const { user } = useAuth();

  // Natively fetch data if we're on the standalone `/offer-letter?id=xyz` route
  const { loan: fetchedLoan, isLoadingLoan } = useQuickLoan(urlId ?? "", {
    enabled: !loanProp && !!urlId,
  });

  const activeLoan = loanProp || fetchedLoan;
  const hasLoanAccountNumber = !!activeLoan?.loan_account_number;

  const { schedule: fetchedSchedule, isLoadingSchedule } = useLoanRepaymentSchedule(
    urlId ?? "",
    { enabled: !scheduleProp && !!urlId && hasLoanAccountNumber }
  );

  const scheduleToShow = useMemo(() => {
    if (scheduleProp && scheduleProp.length > 0) return scheduleProp;
    if (fetchedSchedule && fetchedSchedule.length > 0) return fetchedSchedule;
    return normalizeRepaymentSchedule(activeLoan?.repayment_schedule ?? null);
  }, [scheduleProp, fetchedSchedule, activeLoan?.repayment_schedule]);

  const handlePrint = () => {
    window.print();
  };

  const isLoading = (!loanProp && isLoadingLoan) || (!scheduleProp && isLoadingSchedule);

  if (isLoading) {
    return (
      <div className="grid place-items-center min-h-[40vh] w-full">
        <HashLoader size={40} className="text-deepBlue" />
      </div>
    );
  }

  if (!activeLoan) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <p>No valid loan offer found.</p>
      </div>
    );
  }

  const facilityAmount = Number(activeLoan.loan_amount ?? 0);
  const tenorMonths = scheduleToShow.length > 0 ? scheduleToShow.length : 3;
  const totalRepaymentAmount = scheduleToShow.reduce((acc: number, row: any) => acc + parseScheduleAmount(row.Total), 0);
  const interestTotal = scheduleToShow.reduce((acc: number, row: any) => acc + parseScheduleAmount(row.Interest), 0);
  const hasInterestBreakdown = scheduleToShow.length > 0 && scheduleToShow.every((row) => row.Interest != null);
  
  // Calculate total monthly breakdown (if valid schedule exists)
  const averageMonthly = scheduleToShow.length > 0 ? (totalRepaymentAmount / scheduleToShow.length) : 0;

  const creationDate = activeLoan.created_at ? moment(activeLoan.created_at).format("DD/MM/YYYY") : moment().format("DD/MM/YYYY");
  const expiryDate = activeLoan.created_at ? moment(activeLoan.created_at).add(tenorMonths, "month").format("DD/MM/YYYY") : "—";
  
  const formattedAddress = user?.address 
    ? `${user.address.street || ""}, ${user.address.city || ""} ${user.address.state || ""}`.trim().replace(/^,|,$/g, '') 
    : "—";

  return (
    <div className={`w-full font-sans text-gray-800 ${isEmbedded ? 'bg-white' : 'min-h-screen bg-gray-100 py-10 px-4 md:px-10'}`}>
      
      {!isEmbedded && (
        <div className="max-w-4xl mx-auto mb-4 flex justify-end print:hidden">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-deepBlue text-white px-4 py-2 rounded-lg shadow hover:bg-deepBlue/90 transition-all font-medium"
          >
            <Printer size={20} />
            Print / Save as PDF
          </button>
        </div>
      )}

      {/* A4 Document Container */}
      <div className={`max-w-4xl mx-auto bg-white shadow-lg border border-gray-200 print:shadow-none print:border-none print:p-0 ${isEmbedded ? 'shadow-none border-none' : 'p-8 md:p-16'}`}>
        
        {/* Header */}
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center border-b-2 border-gray-800 pb-6 mb-8">
          <div className="flex items-center gap-4 mb-4 md:mb-0">
            {/* Logo Placeholder */}
            <div className="w-16 h-16 bg-blue-900 text-white flex items-center justify-center font-bold text-2xl rounded-tr-xl rounded-bl-xl">
              A
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-extrabold text-blue-900 leading-tight">
                Alert <br /> Microfinance <br /> Bank
              </h1>
            </div>
          </div>
          <div className="text-xs md:text-sm text-right border-l-4 border-orange-500 pl-4">
            <p className="font-bold">HEAD OFFICE:</p>
            <p>123, Herbert Macaulay Way,</p>
            <p>Ebute Metta, Lagos.</p>
            <p>Tel: 02013301818, 07000225378</p>
            <p>www.alertmfb.com.ng</p>
            <p>customercare@alertgroup.com.ng</p>
          </div>
        </header>

        {/* Letter Metadata */}
        <div className="mb-8 text-sm md:text-base">
          <h2 className="text-center text-lg md:text-xl font-bold underline mb-8">OFFER LETTER</h2>
          
          <div className="flex flex-col gap-1 mb-6 font-medium">
            <p>DATE: <span className="font-normal border-b border-dashed border-gray-400 min-w-[200px] inline-block px-2">{creationDate}</span></p>
            <p>CLIENT NAME: <span className="font-normal border-b border-dashed border-gray-400 min-w-[200px] inline-block px-2 uppercase">{user?.firstname} {user?.lastname}</span></p>
            <p>ADDRESS: <span className="font-normal border-b border-dashed border-gray-400 min-w-[200px] inline-block px-2 uppercase">{formattedAddress.length > 2 ? formattedAddress : "N/A"}</span></p>
          </div>

          <p className="mb-4">Dear Sir/Ma,</p>
          <p className="font-bold underline mb-4">OFFER OF CREDIT FACILITY: ₦{formatAsMoney(facilityAmount)}</p>
          <p className="leading-relaxed text-justify mb-6">
            We refer to your application for a credit facility. We are pleased to inform you that the Management of <strong>ALERT MICROFINANCE BANK LIMITED</strong> has approved the granting of your request under the following terms and conditions:
          </p>
        </div>

        {/* Key Terms Table */}
        <div className="mb-8">
          <table className="w-full border-collapse border border-gray-300 text-sm">
            <tbody>
              {[
                { label: "Lender", value: "ALERT Microfinance Bank Limited" },
                { label: "Borrower", value: `${user?.firstname || ""} ${user?.lastname || ""}`.toUpperCase() },
                { label: "Facility Type", value: activeLoan.industry?.toUpperCase() || "FLY NOW PAY LATER" },
                { label: "Facility Amount", value: `₦${formatAsMoney(facilityAmount)}` },
                { label: "Effective Date", value: creationDate },
                { label: "Expiry Date", value: expiryDate },
                { label: "Purpose", value: String(activeLoan.loan_purpose || "CONSUMPTION").toUpperCase() },
                { label: "Tenor", value: `${tenorMonths} Months` },
                { label: "Interest Total", value: hasInterestBreakdown ? `₦${formatAsMoney(interestTotal)}` : "Not provided in the saved schedule" },
                { label: "Repayment Pattern", value: `Monthly repayment of ₦${formatAsMoney(averageMonthly)} (See repayment schedule)` },
                { label: "Total Loan Repayment", value: `₦${formatAsMoney(totalRepaymentAmount)}` },
              ].map((row, idx) => (
                <tr key={idx} className="border-b border-gray-300">
                  <td className="p-2 md:p-3 font-bold bg-gray-50 border-r border-gray-300 w-2/5 md:w-1/3">{row.label}:</td>
                  <td className="p-2 md:p-3">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Conditions */}
        <div className="mb-8 space-y-6 text-sm leading-relaxed text-justify">
          <div>
            <h3 className="font-bold underline mb-2">Conditions Precedent to Drawdown</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Copy of the Offer Letter duly accepted by the Borrower.</li>
              <li>Receipt of all upfront charges payable by the Borrower.</li>
              <li>Receipt of post-dated cheques from the Borrower which is to be presented for payment each month without recourse.</li>
              <li>Duly Executed Deed of Continuing and Unconditional Personal Guarantee to be executed in front of a staff of Alert Microfinance Bank Limited (with or without a Court Order after a 14 (fourteen) days’ notice to call in facility is given be inserted at appropriate places in the Deed of Continuing and Unconditional Personal Guarantee).</li>
              <li>Provision of pledged collaterals as listed on Schedule A of the Security Agreement and/or Deed of conveyance.</li>
              <li>Execution of Repayment Plan.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold underline mb-2">Other Conditions:</h3>
            <ul className="list-decimal pl-5 space-y-2">
              <li>In the event of default by the borrower in repayment of the facility or interest arising thereon or in compliance with the terms and conditions, and covenants contained herein, after the maturity of demand immediate repayment of all outstanding on the facility and or crystallize the security with or without a Court Order after a 14 (fourteen) days’ notice to call in facility is given.</li>
              <li>If any installment or amount due remains unpaid after the due date, an additional 1% monthly fee shall be charged on the amount due and unpaid until full repayment.</li>
              <li>In the event of default, the Bank in its absolute discretion and without the consent of, or notice to the Borrower reserves the right to apply the guarantee and or seek repayment through any means, and any cost incurred in that process shall be debited to the Borrower’s account.</li>
              <li>The Borrower shall pay ₦5,000.00 in addition to COT for any of his cheques dishonored upon presentation for payment by the lender.</li>
              <li>The Bank reserves the right and in its absolute discretion, to vary, alter, or amend any of the terms and conditions of this facility without prior notification or consent of the borrower.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold underline mb-2">Events of Default:</h3>
            <p className="mb-2">The occurrence of any of the following shall cause all outstanding(s) under the facility to be due and immediately repayable:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>If the Borrower fails to settle when due, any outstanding amount owed to and advised by the Bank; or</li>
              <li>If in the opinion of the Bank, there is an adverse material change in the employment or financial condition of the Borrower; or</li>
              <li>If any extraordinary situation arises such that the continuance of the transaction in the opinion of the Bank makes it impossible for the Borrower to discharge its obligation.</li>
              <li>The Borrower will bear the recovery cost in case of default that requires recovery action.</li>
              <li>That I voluntarily issue post-dated cheques signed by me, and to be filled by the bank with the amount of loan principal and interest in arrears or default.</li>
              <li>This cheque can be presented for payment without a recourse to me in case of default as the account will always be funded.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold underline mb-2">Insurance:</h3>
            <p>
              The borrower will bear the cost of insurance while the bank will insure the loan with a reputable insurance company. However, it is important to note that indemnity under insurance claims is not a hundred percent (100%), and it is the responsibility of the borrower to repay the outstanding loan balance that is not refunded under insurance indemnity and claim.
            </p>
          </div>

          <p className="font-bold">This offer is subject to the availability of funds.</p>

          <p>
            If the above terms and conditions are acceptable to you, please execute and return to us the attached duplicate copies of this Offer Letter as an indication of such acceptance.
          </p>
          <p>
            The Bank reserves the right to regard the offer contained herein as lapsed should the executed duplicate copies not be returned to us within two weeks of the date of this offer letter.
          </p>
        </div>

        {/* Signatures */}
        <div className="mb-12 pt-8">
          <p className="mb-8">Yours Faithfully;</p>
          <p className="font-bold mb-12">For: ALERT MICROFINANCE BANK LIMITED</p>
          
          <div className="flex justify-between items-end">
            <div className="w-[40%]">
              <div className="border-b border-gray-800 mb-2"></div>
              <p className="font-bold text-sm">Branch Manager</p>
            </div>
            <div className="w-[40%]">
              <div className="border-b border-gray-800 mb-2"></div>
              <p className="font-bold text-sm">Loan Officer</p>
            </div>
          </div>
        </div>

        {/* Page Break for printing */}
        <div className="print:break-before-page"></div>

        {/* Memorandum of Acceptance */}
        <div className="mb-16 border-t-2 border-gray-800 pt-8 mt-12">
          <h2 className="text-xl font-bold text-center underline mb-8">MEMORANDUM OF ACCEPTANCE</h2>
          <p className="leading-relaxed mb-8 text-justify text-sm md:text-base">
            I <span className="inline-block border-b border-gray-800 min-w-[200px] md:min-w-[300px] px-2 font-medium uppercase text-center">{user?.firstname || ""} {user?.lastname || ""}</span> at this moment accept this offer under the above-stated terms and conditions, and also authorize Alert Microfinance Bank Limited to recover its total outstanding balance from any of my accounts in other banks using my BVN in the event of default in payment of this loan. Reserves the right to debit my account with any Financial Institution using the BVN provided in the event of default in repayment of this facility.
          </p>
          <div className="w-1/2 md:w-1/3">
            <div className="border-b border-gray-800 mb-2 h-8"></div>
            <p className="font-bold text-sm">Signature and Date</p>
          </div>
        </div>

        {/* Repayment Schedule */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-center underline mb-6">LOAN REPAYMENT SCHEDULE</h2>
          
          {/* Schedule Header Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 md:gap-y-4 mb-6 text-sm border p-4 border-gray-300 bg-gray-50/50">
            <div><span className="font-bold">Customer Name:</span> {user?.firstname?.toUpperCase()} {user?.lastname?.toUpperCase()}</div>
            <div><span className="font-bold">Phone Number:</span> {user?.phone || "N/A"}</div>
            <div><span className="font-bold">Currency:</span> NGN</div>
            <div><span className="font-bold">Tenure in Month:</span> {tenorMonths}</div>
            <div><span className="font-bold">Loan Product:</span> {activeLoan.industry?.toUpperCase()} (BNPL)</div>
            <div><span className="font-bold">Date Approved:</span> {creationDate}</div>
            <div><span className="font-bold">Amount Approved:</span> {formatAsMoney(facilityAmount)}</div>
            <div><span className="font-bold">Total Amount Payable:</span> {formatAsMoney(totalRepaymentAmount)}</div>
            <div><span className="font-bold">Interest Percentage:</span> {hasInterestBreakdown && facilityAmount > 0 ? `${((interestTotal / facilityAmount) * 100).toFixed(1)}% of financed amount` : "Not provided in the saved schedule"}</div>
            <div><span className="font-bold">Duration:</span> {tenorMonths} Months</div>
          </div>

          {/* Schedule Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-xs md:text-sm text-center">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  <th className="border border-gray-300 p-2 md:p-3 whitespace-nowrap">S.No.</th>
                  <th className="border border-gray-300 p-2 md:p-3 whitespace-nowrap">Date</th>
                  <th className="border border-gray-300 p-2 md:p-3 whitespace-nowrap">Monthly Installment</th>
                  <th className="border border-gray-300 p-2 md:p-3 whitespace-nowrap">Principal</th>
                  <th className="border border-gray-300 p-2 md:p-3 whitespace-nowrap">Interest</th>
                  <th className="border border-gray-300 p-2 md:p-3 whitespace-nowrap">Remaining Total</th>
                </tr>
              </thead>
              <tbody>
                {scheduleToShow.length > 0 ? scheduleToShow.map((row: any, idx: number) => {
                  
                  // The backend typically provides a remaining balance per installment or we calculate manually.
                  // Since QuickLoanRepaymentScheduleItem yields `Total`, `Principal`, `Interest`.
                  const itemTotal = parseScheduleAmount(row.Total);
                  const itemPrincipal = parseScheduleAmount(row.Principal);
                  const itemInterest = parseScheduleAmount(row.Interest);
                  
                  // Calculate reverse balance: TotalRepayment - (Installment * (idx + 1))
                  const remainingBalance = totalRepaymentAmount - scheduleToShow.slice(0, idx + 1).reduce((sum, item) => sum + parseScheduleAmount(item.Total), 0);

                  return (
                    <tr key={row.Id || idx} className="border border-gray-300 hover:bg-gray-50">
                      <td className="border border-gray-300 p-2">{idx + 1}</td>
                      <td className="border border-gray-300 p-2 whitespace-nowrap">{formatPaymentDue(row.PaymentDueDate || "")}</td>
                      <td className="border border-gray-300 p-2 font-medium">{formatAsMoney(itemTotal)}</td>
                      <td className="border border-gray-300 p-2">{row.Principal != null ? formatAsMoney(itemPrincipal) : "—"}</td>
                      <td className="border border-gray-300 p-2">{row.Interest != null ? formatAsMoney(itemInterest) : "—"}</td>
                      <td className="border border-gray-300 p-2 font-semibold">
                        {remainingBalance > 0 ? formatAsMoney(remainingBalance) : 0}
                      </td>
                    </tr>
                  )
                }) : (
                  <tr>
                    <td colSpan={6} className="border border-gray-300 p-4 text-gray-500">No repayment schedule data available.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default OfferLetter;
